import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api, money, formatApiError } from "../../api";
import { formatDeliveryTime } from "../../utils/whatsappBill";

const STATUSES = ["new", "whatsapp_sent", "awaiting_confirmation", "confirmed", "processing", "ready", "shipped", "completed", "cancelled"];

export default function AdminOrders() {
  const [orders, setOrders] = useState([]);
  const [statusFilter, setStatusFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = statusFilter ? { status: statusFilter } : {};
      const { data } = await api.get("/api/admin/orders", { params });
      setOrders(data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    load();
  }, [load]);

  const changeStatus = async (order, status) => {
    try {
      await api.patch(`/api/admin/orders/${order.order_id}/status`, { status });
      toast.success("Status updated");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const remove = async (order) => {
    if (!window.confirm(`Delete order ${order.order_id}?`)) return;
    try {
      await api.delete(`/api/admin/orders/${order.order_id}`);
      toast.success("Order deleted");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div className="admin-page">
      <div className="admin-page-head">
        <div>
          <span className="eyebrow">Fulfilment</span>
          <h1>Orders.</h1>
        </div>
      </div>
      <div className="admin-toolbar">
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          data-testid="admin-orders-status-filter"
        >
          <option value="">All statuses</option>
          {STATUSES.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
      </div>
      {loading ? (
        <p className="empty-state">Loading…</p>
      ) : orders.length ? (
        <div className="admin-table" data-testid="admin-orders-table">
          <div className="admin-table-head admin-orders-head">
            <span>Order</span>
            <span>Customer</span>
            <span>Items</span>
            <span>Total</span>
            <span>Status</span>
            <span></span>
          </div>
          {orders.map((o) => (
            <div key={o.id}>
              <div className="admin-table-row admin-orders-row" data-testid={`admin-order-row-${o.order_id}`}>
                <div>
                  <strong>{o.order_id}</strong>
                  <span className="eyebrow">{new Date(o.created_at).toLocaleString()}</span>
                </div>
                <div>
                  <strong>{o.customer_name}</strong>
                  <span className="eyebrow">{o.customer_phone}</span>
                </div>
                <span>{o.items.reduce((a, x) => a + x.quantity, 0)} pcs</span>
                <span>{money(o.total)}</span>
                <select
                  className={`status-select status-${o.status}`}
                  value={o.status}
                  onChange={(e) => changeStatus(o, e.target.value)}
                  data-testid={`admin-order-status-${o.order_id}`}
                >
                  {STATUSES.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
                <div className="row-actions">
                  <button
                    onClick={() => setExpanded(expanded === o.order_id ? null : o.order_id)}
                    data-testid={`admin-order-expand-${o.order_id}`}
                  >
                    {expanded === o.order_id ? "Hide" : "View"}
                  </button>
                  <button onClick={() => remove(o)} data-testid={`admin-order-delete-${o.order_id}`}>
                    Delete
                  </button>
                </div>
              </div>
              {expanded === o.order_id && (
                <div className="admin-order-detail" data-testid={`admin-order-detail-${o.order_id}`}>
                  <div>
                    <span className="eyebrow">Delivery</span>
                    <p>{o.address}</p>
                    {o.notes && (
                      <>
                        <span className="eyebrow">Notes</span>
                        <p>{o.notes}</p>
                      </>
                    )}
                    {o.customer_email && (
                      <>
                        <span className="eyebrow">Email</span>
                        <p>{o.customer_email}</p>
                      </>
                    )}
                  </div>
                  <div>
                    <span className="eyebrow">Items</span>
                    {o.items.map((it, idx) => (
                      <div className="order-item-row" key={idx}>
                        <span>
                          {it.name}
                          {it.look_number ? ` (${it.look_number})` : ""}
                        </span>
                        <span>
                          {it.size}
                          {it.color ? ` · ${it.color}` : ""}
                          {` · Delivery ${formatDeliveryTime(it.delivery_time_days)}`}
                        </span>
                        <span>×{it.quantity}</span>
                        <strong>{money(it.price * it.quantity)}</strong>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      ) : (
        <p className="empty-state">No orders yet.</p>
      )}
    </div>
  );
}
